import React, { useState, useEffect, ReactNode, createContext } from 'react';

import { useWalletClient, usePublicClient } from 'wagmi';
import { contractAddresses } from '../helpers/config/contracts.config';
import { getContract } from 'viem';
import ddpAbi from '../abi/ddp.json';
import usdcAbi from '../abi/usdcBase.json';
import erc20Abi from '../abi/erc20.json';
import nrsAbi from '../abi/nrsPolygon.json';
import lpStakingAbi from '../abi/lpStaking.json';
import lockNrsAbi from '../abi/lockNrsPolygon.json';
import nrsLockWithdrawalTimelockAbi from '../abi/nrsLockWithdrawalTimelockPolygon.json';
import aerodromePoolAbi from '../abi/aerodromePool.json';


type ContractProviderProps = {
    children: ReactNode;
};

type ContractsContextType = {
    ddpContract: any | null;
    usdcContract: any | null;
    nrsContract: any | null;
    lockNrsContract: any | null;
    lpNrsUsdcContract: any | null;
    lpNrsUsdcStakingContract: any | null;
    lpNxusdUsdcContract: any | null;
    lpNxusdUsdcStakingContract: any | null;
    nrsLockWithdrawalTimelockContract: any | null;
    lpNrsUsdcPoolContract: any | null;
    lpNxusdUsdcPoolContract: any | null;
};


export const ContractContext = createContext<ContractsContextType>(
    {
        ddpContract: null,
        usdcContract: null,
        nrsContract: null,
        lockNrsContract: null,
        lpNrsUsdcContract: null,
        lpNrsUsdcStakingContract: null,
        lpNxusdUsdcContract: null,
        lpNxusdUsdcStakingContract: null,
        nrsLockWithdrawalTimelockContract: null,
        lpNrsUsdcPoolContract: null,
        lpNxusdUsdcPoolContract: null
    });

const ContractsProvider: React.FC<ContractProviderProps> = ({ children }) => {
    const [ddpContract, setDdpContract] = useState<any | null>(null);
    const [usdcContract, setUsdcContract] = useState<any | null>(null);
    const [nrsContract, setNrsContract] = useState<any | null>(null);
    const [lockNrsContract, setLockNrsContract] = useState<any | null>(null);
    const [lpNrsUsdcContract, setLpNrsUsdcContract] = useState<any | null>(null);
    const [lpNrsUsdcStakingContract, setLpNrsUsdcStakingContract] = useState<any | null>(null);
    const [lpNxusdUsdcContract, setLpNxusdUsdcContract] = useState<any | null>(null);
    const [lpNxusdUsdcStakingContract, setLpNxusdUsdcStakingContract] = useState<any | null>(null);
    const [nrsLockWithdrawalTimelockContract, setNrsLockWithdrawalTimelockContract] = useState<any | null>(null);
    const [lpNrsUsdcPoolContract, setLpNrsUsdcPoolContract] = useState<any | null>(null);
    const [lpNxusdUsdcPoolContract, setLpNxusdUsdcPoolContract] = useState<any | null>(null);
    const { data: signer } = useWalletClient();
    const provider = usePublicClient();

    useEffect(() => {

        const ddpInstance = (getContract as any)({
            address: contractAddresses.base.ddp,
            abi: ddpAbi,
            client: {
                public: provider,
                wallet: signer
            }
        });

        setDdpContract(ddpInstance)

        const usdcInstance = (getContract as any)({
            address: contractAddresses.base.usdc,
            abi: usdcAbi,
            client: {
                public: provider,
                wallet: signer
            }
        });
        setUsdcContract(usdcInstance);

        const nrsInstance = (getContract as any)({
            address: contractAddresses.base.nrs,
            abi: nrsAbi,
            client: {
                public: provider,
                wallet: signer
            }
        });
        setNrsContract(nrsInstance);

        const lockNrsInstance = (getContract as any)({
            address: contractAddresses.base.lockNrs,
            abi: lockNrsAbi,
            client: {
                public: provider,
                wallet: signer
            }
        });
        setLockNrsContract(lockNrsInstance);

        const lpNrsUsdcContractInstance = (getContract as any)({
            address: contractAddresses.base.lpNrsUsdcContract,
            abi: erc20Abi,
            client: {
                public: provider,
                wallet: signer
            }
        });
        setLpNrsUsdcContract(lpNrsUsdcContractInstance);

        const lpNrsUsdcStakingContractInstance = (getContract as any)({
            address: contractAddresses.base.lpNrsUsdcStakingContract,
            abi: lpStakingAbi,
            client: {
                public: provider,
                wallet: signer
            }
        });
        setLpNrsUsdcStakingContract(lpNrsUsdcStakingContractInstance);

        const lpNxusdUsdcContractInstance = (getContract as any)({
            address: contractAddresses.base.lpNxusdUsdcContract,
            abi: erc20Abi,
            client: {
                public: provider,
                wallet: signer
            }
        });
        setLpNxusdUsdcContract(lpNxusdUsdcContractInstance);

        const lpNxusdUsdcStakingContractInstance = (getContract as any)({
            address: contractAddresses.base.lpNxusdUsdcStakingContract,
            abi: lpStakingAbi,
            client: {
                public: provider,
                wallet: signer
            }
        });
        setLpNxusdUsdcStakingContract(lpNxusdUsdcStakingContractInstance);

        const nrsLockWithdrawalTimelockInstance = (getContract as any)({
            address: contractAddresses.base.nrsLockWithdrawalTimelock,
            abi: nrsLockWithdrawalTimelockAbi,
            client: {
                public: provider,
                wallet: signer
            }
        });
        setNrsLockWithdrawalTimelockContract(nrsLockWithdrawalTimelockInstance);

        const lpNrsUsdcPoolInstance = (getContract as any)({
            address: contractAddresses.base.lpNrsUsdcPoolContract,
            abi: aerodromePoolAbi,
            client: {
                public: provider,
                wallet: signer
            }
        });
        setLpNrsUsdcPoolContract(lpNrsUsdcPoolInstance);

        const lpNxusdUsdcPoolInstance = (getContract as any)({
            address: contractAddresses.base.lpNxusdUsdcPoolContract,
            abi: aerodromePoolAbi,
            client: {
                public: provider,
                wallet: signer
            }
        });
        setLpNxusdUsdcPoolContract(lpNxusdUsdcPoolInstance);

    }, [signer, provider]);

    return (
        <ContractContext.Provider value={{
            ddpContract,
            usdcContract,
            nrsContract,
            lockNrsContract,
            lpNrsUsdcContract,
            lpNrsUsdcStakingContract,
            lpNxusdUsdcContract,
            lpNxusdUsdcStakingContract,
            nrsLockWithdrawalTimelockContract,
            lpNrsUsdcPoolContract,
            lpNxusdUsdcPoolContract
        }}>
            {children}
        </ContractContext.Provider>
    );
};

export default ContractsProvider;
